import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Waypoints, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SsoCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const attempted = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects — run the handoff exactly once so the
    // hash-stripping from run 1 doesn't make run 2 flash "Missing login token".
    if (attempted.current) return;
    attempted.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const match = hash.match(/#token=([^&]+)/);
      const token = match ? match[1] : null;
      if (!token) {
        setError("Missing login token. Please use the link from Job Basket again.");
        return;
      }
      // Strip the token from the URL/history immediately.
      window.history.replaceState({}, document.title, "/sso-callback");
      try {
        localStorage.setItem("pbp_token", token);
        const { data } = await api.get("/auth/me");
        await login(token, data.user);
        navigate("/app", { replace: true });
      } catch (e) {
        localStorage.removeItem("pbp_token");
        setError(formatApiError(e.response?.data?.detail) || "This login link is invalid or expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F6F8] mesh-grid flex items-center justify-center px-4" data-testid="sso-callback-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-8 w-full max-w-sm text-center"
      >
        <div className="mx-auto w-fit">
          <img src="/logo-jbn-icon.png" alt="JobBasket Network" className="w-12 h-12 object-contain" />
        </div>
        {error ? (
          <>
            <ShieldAlert className="w-8 h-8 text-amber-400 mx-auto mt-5" />
            <h1 className="text-xl font-bold font-display mt-3" data-testid="sso-callback-error">Couldn't sign you in</h1>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
            <Link
              to="/auth"
              data-testid="sso-callback-back-to-login"
              className="inline-block mt-6 text-sm px-5 py-2.5 rounded-lg bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors"
            >
              Sign in with email instead
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-amber-500 mx-auto mt-5 animate-spin" />
            <h1 className="text-xl font-bold font-display mt-3" data-testid="sso-callback-signing-in">Signing you in…</h1>
            <p className="text-sm text-slate-500 mt-2">Securely connecting your Job Basket account.</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
